import { api } from "@/lib/api";

/**
 * The day counted over the roster, and the days that never arrived.
 *
 * Both come from the server and neither can be worked out on the device
 * (Z-13, O-18). The site manager's home screen used to render a hardcoded
 * zero beside a real total — "0 od 3" on a day whose hours were all in — and
 * it would have read that for ever, because nothing was ever asked.
 *
 * One request, not two. A manager who runs two sites needs them added up, and
 * adding them up on the phone means the phone has to know which sites are his
 * — which is the server's question, not the device's. The same summary is
 * what the management dashboard will read later.
 */

export interface SazetakGradilista {
  gradiliste_id: string;
  gradiliste: string;
  na_rasporedu: number;
  evidentirano: number;
  preostalo: number;
}

export interface Sazetak {
  datum: string;
  na_rasporedu: number;
  evidentirano: number;
  preostalo: number;
  gradilista: SazetakGradilista[];
}

/** A day whose hours never arrived, and how old the hole is (O-18). */
export interface Rupa {
  gradiliste_id: string;
  gradiliste: string;
  voditelj: string | null;
  datum: string;
  dana: number;
  bez_evidencije: number;
  na_rasporedu: number;
  /** Past the seventh day: NN 55/24 čl. 15, a breach rather than a reminder. */
  prekrsaj: boolean;
}

export async function dohvatiSazetak(
  datum?: string,
  signal?: AbortSignal,
): Promise<Sazetak> {
  const response = await api.get<Sazetak>("/sati/sazetak", {
    params: datum ? { datum } : undefined,
    signal,
  });
  return response.data;
}

/**
 * The gaps this caller is meant to see.
 *
 * `pragDana` is who is asking rather than a filter for its own sake: three
 * days on the manager's own screen, five on the personnel dashboard (O-18).
 * Left out, the server applies the manager's, which is the lower of the two.
 */
export async function dohvatiRupe(
  pragDana?: number,
  signal?: AbortSignal,
): Promise<Rupa[]> {
  const response = await api.get<Rupa[]>("/sati/rupe", {
    params: pragDana === undefined ? undefined : { prag_dana: pragDana },
    signal,
  });
  return response.data;
}

const DANI = ["nedjelju", "ponedjeljak", "utorak", "srijedu", "četvrtak", "petak", "subotu"];

/**
 * "Za ponedjeljak nema evidencije" — the wording O-18 asks for.
 *
 * A weekday name for the last week, because that is how somebody remembers a
 * day he was on a site; a date beyond that, because "za utorak" stops being
 * unambiguous once two Tuesdays have passed.
 */
export function opisRupe(rupa: Rupa): string {
  const dan = new Date(`${rupa.datum}T00:00:00`);
  if (rupa.dana <= 6) {
    return `Za ${DANI[dan.getDay()] ?? "taj dan"} nema evidencije`;
  }
  const formatirano = `${String(dan.getDate()).padStart(2, "0")}.${String(
    dan.getMonth() + 1,
  ).padStart(2, "0")}.`;
  return `Za ${formatirano} nema evidencije`;
}
